/**
 * Multi-Level Logging System
 *
 * Environment-based logging with feature-specific debug flags
 * - Development: All logs enabled
 * - Alpha: Important logs only (auth, sync, errors)
 * - Production: Errors only
 *
 * Usage:
 *   import { logger } from './lib/logger';
 *   logger.auth('User signed in:', user.email);
 *   logger.sync('Syncing notes...');
 *   logger.error('API', 'Request failed:', error);
 */

export const LogLevel = {
  DEBUG: 0,   // Everything (dev only)
  INFO: 1,    // Important events (Alpha/Beta)
  WARN: 2,    // Warnings (production)
  ERROR: 3,   // Errors only (production)
  NONE: 4,    // Silence all logs (production)
} as const;

export type LogLevel = typeof LogLevel[keyof typeof LogLevel];

/**
 * Get log level based on environment
 */
const getLogLevel = (): LogLevel => {
  const env = import.meta.env.MODE;
  const appEnv = import.meta.env.VITE_APP_ENV;

  // Explicit app environment takes precedence
  if (appEnv === 'alpha' || appEnv === 'beta') return LogLevel.INFO;
  if (appEnv === 'production') return LogLevel.ERROR;

  // Fall back to Vite MODE
  if (env === 'development') return LogLevel.DEBUG;
  if (env === 'production') return LogLevel.ERROR;

  return LogLevel.WARN;
};

const currentLogLevel = getLogLevel();

/**
 * Feature-specific debug flags
 * Can be enabled in production for debugging specific issues
 */
const debugFlags = {
  auth: import.meta.env.VITE_DEBUG_AUTH === 'true',
  sync: import.meta.env.VITE_DEBUG_SYNC === 'true',
  yjs: import.meta.env.VITE_DEBUG_YJS === 'true',
  api: import.meta.env.VITE_DEBUG_API === 'true',
};

/**
 * Format timestamp for logs
 */
const timestamp = (): string => {
  const now = new Date();
  return now.toISOString().split('T')[1].split('.')[0]; // HH:MM:SS
};

/**
 * Logger utility with environment-based filtering
 */
export const logger = {
  /**
   * Debug logs - Development only
   * Use for verbose debugging information
   */
  debug: (category: string, ...args: any[]) => {
    if (currentLogLevel <= LogLevel.DEBUG) {
      console.log(`[${timestamp()}] [DEBUG:${category}]`, ...args);
    }
  },

  /**
   * Info logs - Development + Alpha/Beta
   * Use for important events and state changes
   */
  info: (category: string, ...args: any[]) => {
    if (currentLogLevel <= LogLevel.INFO) {
      console.log(`[${timestamp()}] [INFO:${category}]`, ...args);
    }
  },

  /**
   * Warning logs - All environments except production
   * Use for recoverable errors and unexpected states
   */
  warn: (category: string, ...args: any[]) => {
    if (currentLogLevel <= LogLevel.WARN) {
      console.warn(`[${timestamp()}] [WARN:${category}]`, ...args);
    }
  },

  /**
   * Error logs - All environments
   * Use for errors and exceptions
   */
  error: (category: string, ...args: any[]) => {
    if (currentLogLevel <= LogLevel.ERROR) {
      console.error(`[${timestamp()}] [ERROR:${category}]`, ...args);
    }
  },

  /**
   * Authentication logs
   * Can be enabled in production via VITE_DEBUG_AUTH=true
   */
  auth: (...args: any[]) => {
    if (debugFlags.auth || currentLogLevel <= LogLevel.DEBUG) {
      console.log(`[${timestamp()}] [AUTH]`, ...args);
    }
  },

  /**
   * Sync engine logs
   * Can be enabled in production via VITE_DEBUG_SYNC=true
   */
  sync: (...args: any[]) => {
    if (debugFlags.sync || currentLogLevel <= LogLevel.DEBUG) {
      console.log(`[${timestamp()}] [SYNC]`, ...args);
    }
  },

  /**
   * Yjs collaboration logs
   * Can be enabled in production via VITE_DEBUG_YJS=true
   */
  yjs: (...args: any[]) => {
    if (debugFlags.yjs || currentLogLevel <= LogLevel.DEBUG) {
      console.log(`[${timestamp()}] [YJS]`, ...args);
    }
  },

  /**
   * API request/response logs
   * Can be enabled in production via VITE_DEBUG_API=true
   */
  api: (method: string, endpoint: string, ...args: any[]) => {
    if (debugFlags.api || currentLogLevel <= LogLevel.DEBUG) {
      console.log(`[${timestamp()}] [API] ${method} ${endpoint}`, ...args);
    }
  },

  /**
   * Performance measurement logs
   * Always enabled in dev/alpha/beta, disabled in production
   */
  perf: (label: string, duration: number) => {
    if (currentLogLevel <= LogLevel.INFO) {
      const formattedDuration = duration < 1000
        ? `${duration.toFixed(2)}ms`
        : `${(duration / 1000).toFixed(2)}s`;
      console.log(`[${timestamp()}] [PERF] ${label}: ${formattedDuration}`);
    }
  },

  /**
   * Performance measurement helper
   * Usage:
   *   const end = logger.perfStart();
   *   // ... do work ...
   *   end('Operation name');
   */
  perfStart: () => {
    const start = performance.now();
    return (label: string) => {
      const duration = performance.now() - start;
      logger.perf(label, duration);
    };
  },

  /**
   * Get current log level (for debugging)
   */
  getLevel: (): LogLevel => currentLogLevel,

  /**
   * Get debug flags status (for debugging)
   */
  getFlags: () => ({ ...debugFlags }),
};

// Log environment configuration on startup (dev/alpha only)
if (currentLogLevel <= LogLevel.INFO) {
  const env = import.meta.env.MODE;
  const appEnv = import.meta.env.VITE_APP_ENV || 'not set';
  const levelName = Object.entries(LogLevel).find(([, val]) => val === currentLogLevel)?.[0] || 'UNKNOWN';
  console.log(`[Logger] Environment: ${env} | App Env: ${appEnv} | Log Level: ${levelName}`);

  const enabledFlags = Object.entries(debugFlags)
    .filter(([, enabled]) => enabled)
    .map(([flag]) => flag);

  if (enabledFlags.length > 0) {
    console.log(`[Logger] Debug flags enabled:`, enabledFlags.join(', '));
  }
}
